<template>
  <div>
    <div class="card">
      <h5 class="card-header">Recalculate Test</h5>
      <div class="card-body">
        <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
          <div class="form-group">
            <label for="referenceNumber" class="">Reference Number</label>
            <input
              type="number"
              v-model="referenceNumber"
              class="form-control"
              id="referenceNumber"
              placeholder="Reference Number"
              required
              :disabled="loadingMessage"
              @keyup.enter="submit"
            />
            <div class="invalid-feedback">
              Please provide a reference number.
            </div>
            <button type="submit" class="btn btn-primary mt-3" @click="submit" :disabled="loadingMessage" @keyup.enter="submit">
              <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Recalculate
            </button>
          </div>
        </form>
      </div>
    </div>
    <ErrorSummary :error="error" />
    <div class="card mt-3" v-if="showResults">
      <h5 class="card-header">Results</h5>
      <div class="card-body">
        <div v-if="this.reportScore">
          <h5>Report Information</h5>
          <div class="row ml-2">
            <div class="form-group col-4">
              <label for="organizationName" class="col-form-label">Organization Name</label>
              <input
                type="text"
                readonly
                placeholder="---"
                class="form-control-plaintext"
                id="organizationName"
                v-model="this.reportScore.organizationDisplayName"
              />
            </div>
            <div class="form-group col-4">
              <label for="referenceNumber" class="col-form-label">Reference Number</label>
              <input type="text" readonly placeholder="---" class="form-control-plaintext" id="referenceNumber" v-model="this.reportScore.referenceNumber" />
            </div>
            <div class="form-group col-4">
              <label for="registrationId" class="col-form-label">Registration Id</label>
              <input type="text" readonly placeholder="---" class="form-control-plaintext" id="registrationId" v-model="this.reportScore.registrationId" />
            </div>
          </div>
          <h5 class="mt-3">Previous Report Scores</h5>
          <div class="row ml-2">
            <div class="form-group col-4">
              <label for="previousOriginalScore" class="col-form-label">Original Score</label>
              <input
                type="text"
                readonly
                placeholder="---"
                class="form-control-plaintext"
                :class="scoreClass(this.reportScore.originalSummary.originalScore)"
                id="previousOriginalScore"
                v-model="this.reportScore.originalSummary.originalScore.scoreDisplayName"
              />
            </div>
            <div class="form-group col-4">
              <label for="previousAdjustedScore" class="col-form-label">Adjusted Score</label>
              <input
                type="text"
                readonly
                placeholder="---"
                class="form-control-plaintext"
                :class="scoreClass(this.reportScore.originalSummary.adjustedScore)"
                id="previousAdjustedScore"
                v-model="this.reportScore.originalSummary.adjustedScore.scoreDisplayName"
              />
            </div>
            <div class="form-group col-4">
              <label for="previousConsistencyScore" class="col-form-label">Consistency Score</label>
              <input
                type="text"
                readonly
                placeholder="---"
                class="form-control-plaintext"
                id="previousConsistencyScore"
                v-model="this.reportScore.originalConsistency.score"
              />
            </div>
          </div>
          <h5 class="mt-3">Recalculated Report Scores</h5>
          <div class="row ml-2">
            <div class="form-group col-4">
              <label for="newOriginalScore" class="col-form-label">Original Score</label>
              <input
                type="text"
                readonly
                placeholder="---"
                class="form-control-plaintext"
                :class="scoreClass(this.reportScore.recalculatedSummary.originalScore)"
                id="newOriginalScore"
                v-model="this.reportScore.recalculatedSummary.originalScore.scoreDisplayName"
              />
            </div>
            <div class="form-group col-4">
              <label for="newAdjustedScore" class="col-form-label">Adjusted Score</label>
              <input
                type="text"
                readonly
                placeholder="---"
                class="form-control-plaintext"
                :class="scoreClass(this.reportScore.recalculatedSummary.adjustedScore)"
                id="newAdjustedScore"
                v-model="this.reportScore.recalculatedSummary.adjustedScore.scoreDisplayName"
              />
            </div>
            <div class="form-group col-4">
              <label for="newConsistencyScore" class="col-form-label">Consistency Score</label>
              <input
                type="text"
                readonly
                placeholder="---"
                class="form-control-plaintext"
                id="newConsistencyScore"
                v-model="this.reportScore.recalculatedConsistency.score"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    ErrorSummary,
  },
  data() {
    return {
      loadingMessage: null,
      referenceNumber: null,
      reportScore: null,
      error: null,
      formWasValidated: false,
    };
  },
  computed: {
    showResults() {
      return this.reportScore != null;
    },
  },
  methods: {
    reset() {
      this.loadingMessage = null;
      this.referenceNumber = null;
      this.reportScore = null;
      this.error = null;
      this.formWasValidated = false;
    },
    scoreClass(score) {
      if (score.scoreCode === "above-conditions") return ["text-success"];
      if (score.scoreCode === "requires-clarification") return ["text-warning"];
      if (score.scoreCode === "below-conditions") return ["text-danger"];
      if (score.scoreCode === "inconclusive") return [];
      if (score.scoreCode === "declined") return ["text-danger"];
      return [];
    },
    submit() {
      const payload = {
        referenceNumber: parseInt(this.referenceNumber),
      };

      // reset
      this.reset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // api
      this.loadingMessage = "Recalculating...";
      this.$axios
        .post(`/v1/admin/test/recalculate`, payload)
        .then(res => {
          this.successMessage = "Recalculation successful!";
          this.reportScore = res.data;
        })
        .catch(err => (this.error = err))
        .finally(() => {
          this.loadingMessage = null;
          this.referenceNumber = null;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.form-group {
  margin-bottom: 0;
}

input[readonly="readonly"] {
  outline: 0px transparent;
}

textarea[readonly="readonly"] {
  outline: 0px transparent;
}
</style>
